<template>
  <AppModalDialog hash="crop-image" close-hash="profile" @closed="handleClosed">
    <div class="container">
      <h2>Crop image</h2>
      <div class="cropWrap">
        <!-- only once an image is chosen (as CropPortrait's v-if="sourceUrl"): mounted on
             every page, it attached window mouse and touch listeners for nothing -->
        <Cropper
          v-if="pendingUrl"
          ref="cropper"
          class="cropper"
          :src="pendingUrl"
          :stencil-component="CircleStencil"
          :stencil-props="{aspectRatio: 1, movable: true, resizable: true}"
          :move-image="{mouse: true, touch: true}"
          :resize-image="{touch: true, wheel: {ratio: 0.15}, adjustStencil: false}"
          image-restriction="none"
          :auto-zoom="true"
          @ready="handleReady"
        />
      </div>
      <AppModalZoomSlider v-model="zoomValue" />
    </div>
    <template #footer>
      <ButtonBespoke outstanding :disabled @click="save">Save</ButtonBespoke>
    </template>
  </AppModalDialog>
</template>

<script setup>
import {ref, computed, nextTick, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useUser} from "@clerk/vue";
import {Cropper, CircleStencil} from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import AppModalDialog from "@/components/App/Modal/Dialog/index";
import ButtonBespoke from "@/components/ButtonBespoke";
import AppModalZoomSlider from "@/components/App/Modal/ZoomSlider";
import {useLog} from "@/composables/useLog";
import {useAvatarCropState} from "@/composables/useAvatarCropState";

const log = useLog("CropImage");
const route = useRoute();
const router = useRouter();
const {isLoaded, isSignedIn, user} = useUser();
const {pendingFile, pendingUrl, clear} = useAvatarCropState();

const cropper = ref(null);
const zoomValue = ref(0.5);
const lastZoomValue = ref(0.5);
const isSaving = ref(false);
const isCropperReady = ref(false);

const canSave = computed(() => {
  if (!pendingUrl.value) return false;
  if (!isCropperReady.value) return false; // without the cropper's result, no crop is saved
  if (!isLoaded.value) return false;
  if (!isSignedIn.value) return false;
  return typeof user.value?.setProfileImage === "function";
});
const disabled = computed(() => isSaving.value || !canSave.value);

function refreshCropper(reason) {
  const api = cropper.value;
  if (!api) return log(`refresh skipped (${reason})`);
  api.refresh();
  log(`refreshed (${reason})`);
}

async function refreshAfterPaint(reason) {
  await nextTick();
  requestAnimationFrame(() => requestAnimationFrame(() => refreshCropper(reason)));
}

function handleReady() {
  isCropperReady.value = true;
  refreshAfterPaint("ready");
}

function getZoomFactor(delta) {
  const speed = 2;
  const amount = Math.min(Math.abs(delta) * speed, 0.5);
  if (amount <= 0) return 1;
  return delta > 0 ? 1 + amount : 1 / (1 + amount);
}

function applyZoom() {
  const api = cropper.value;
  if (!api) return;
  const next = Number(zoomValue.value);
  const prev = Number(lastZoomValue.value);
  if (!Number.isFinite(next) || !Number.isFinite(prev)) return;
  const delta = next - prev;
  if (delta === 0) return;
  lastZoomValue.value = next;
  api.zoom(getZoomFactor(delta));
}

function getCropCoords(result) {
  const {coordinates, image} = result || {};
  if (!coordinates || !image?.width || !image?.height) return null;
  return {
    x: coordinates.left / image.width,
    y: coordinates.top / image.height,
    w: coordinates.width / image.width,
    imgAspect: image.width / image.height,
  };
}

async function save() {
  if (!canSave.value) return log("save ignored: not ready");
  const api = cropper.value;
  if (!api) return log("save ignored: cropper missing");
  if (!pendingFile.value) return log("save ignored: no file");
  const result = api.getResult();
  if (!result) return log("save ignored: no result");
  isSaving.value = true;
  try {
    const coords = getCropCoords(result);
    await user.value.setProfileImage({file: pendingFile.value});
    // Clerk replaces unsafeMetadata as a whole: keep the rest (e.g. the vizbi-2026 record), but
    // not the old photo's portrait rectangle (it's estimated from the new circle until set again)
    const {portraitCrop, ...rest} = user.value.unsafeMetadata || {};
    const unsafeMetadata = coords ? {...rest, avatarCrop: coords} : rest;
    if (coords || portraitCrop) await user.value.update({unsafeMetadata});
    log("save success");
    clear();
    router.push({hash: "#profile"});
  } catch (error) {
    log(`save failed: ${error}`);
  } finally {
    isSaving.value = false;
  }
}

function handleClosed() {
  clear();
}

watch(zoomValue, applyZoom);

watch(
  () => route.hash,
  (hash) => {
    if (hash !== "#crop-image") return;
    lastZoomValue.value = zoomValue.value;
    refreshAfterPaint("hash change");
  },
);

watch(
  () => pendingUrl.value,
  (url) => {
    isCropperReady.value = false; // a new image: ready again once the cropper has loaded it
    if (!url) return;
    refreshAfterPaint("pendingUrl set");
  },
);
</script>

<style scoped>
.container * {
  color: var(--base-black, #000) !important;
}
h2 {
  font-weight: 650;
  margin-bottom: var(--space-small, 0.5rem);
}
.cropWrap {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  margin-bottom: 16px;
}
.cropper {
  width: 100%;
  height: 100%;
}
</style>
